export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const SMARTSHEET_RECONNECT_ERROR_CODE = "NO_ACTIVE_API_KEY";
const SMARTSHEET_REGION_STORAGE_KEY = "grid2.smartsheetRegion";

type SmartsheetRegion = "us" | "eu" | "au";

function isSmartsheetRegion(value: string | null): value is SmartsheetRegion {
  return value === "us" || value === "eu" || value === "au";
}

export function getPreferredSmartsheetRegion(): SmartsheetRegion {
  if (typeof window === "undefined") return "us";
  try {
    const stored = window.localStorage.getItem(SMARTSHEET_REGION_STORAGE_KEY);
    if (isSmartsheetRegion(stored)) return stored;
  } catch {
    // Storage can be unavailable in locked-down browsers; fall back safely.
  }
  return "us";
}

export function rememberPreferredSmartsheetRegion(region: SmartsheetRegion): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SMARTSHEET_REGION_STORAGE_KEY, region);
  } catch {
    // Non-critical preference only.
  }
}

export function buildSmartsheetConnectPath(location: Pick<Location, "pathname" | "search" | "hash">): string {
  const returnTo = `${location.pathname}${location.search}${location.hash}` || "/dashboard";
  const params = new URLSearchParams({
    region: getPreferredSmartsheetRegion(),
    mode: "connect",
    redirect: returnTo,
  });
  return `/api/auth/smartsheet/login?${params.toString()}`;
}

export function redirectToSmartsheetConnect(): void {
  if (typeof window === "undefined") return;
  window.location.href = buildSmartsheetConnectPath(window.location);
}

export function isSmartsheetReconnectError(error: { code?: string; message?: string }): boolean {
  if (error.code === SMARTSHEET_RECONNECT_ERROR_CODE) {
    return true;
  }

  const message = error.message?.toLowerCase() ?? "";
  if (
    error.code === "MISSING_TOKEN" &&
    (message.includes("smartsheet") || message.includes("x-smartsheet-token"))
  ) {
    return true;
  }

  return (
    message.includes("reconnect with smartsheet") ||
    message.includes("no active smartsheet") ||
    message.includes("no active api key found for organization") ||
    message.includes("smartsheet authorization is missing or expired") ||
    message.includes("x-smartsheet-token header is required")
  );
}

type ErrorEnvelope =
  | {
      error?: string | { code?: string; message?: string };
      message?: string;
      code?: string;
    }
  | string
  | null;

const MANAGEMENT_PROXY_HEADER = "X-Grid2-Management-Proxy";
const DEFAULT_API_TIMEOUT_MS = 15_000;

function isManagementAdminPath(path: string): boolean {
  return (
    path === "/mgmt/admin" ||
    path.startsWith("/mgmt/admin/") ||
    path.startsWith("/mgmt/admin?")
  );
}

function normalizeApiPath(path: string): { path: string; usesManagementProxy: boolean } {
  if (isManagementAdminPath(path)) {
    return {
      path: path.replace(/^\/mgmt/, ""),
      usesManagementProxy: true,
    };
  }

  return { path, usesManagementProxy: false };
}

function mergeHeaders(...sources: Array<HeadersInit | undefined>): Headers {
  const headers = new Headers();

  for (const source of sources) {
    if (!source) continue;
    new Headers(source).forEach((value, key) => headers.set(key, value));
  }

  return headers;
}

function timeoutMessage(path: string): string {
  return `Grid2 is taking too long to respond while loading ${path}. Please retry in a moment.`;
}

async function fetchWithTimeout(path: string, options: RequestInit = {}): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), DEFAULT_API_TIMEOUT_MS);

  const upstreamSignal = options.signal;
  const abortFromUpstream = () => controller.abort();
  if (upstreamSignal) {
    if (upstreamSignal.aborted) controller.abort();
    else upstreamSignal.addEventListener("abort", abortFromUpstream, { once: true });
  }

  try {
    return await fetch(path, {
      ...options,
      signal: controller.signal,
    });
  } catch (error) {
    if (controller.signal.aborted && !upstreamSignal?.aborted) {
      throw new ApiError(408, timeoutMessage(path), "REQUEST_TIMEOUT");
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
    upstreamSignal?.removeEventListener("abort", abortFromUpstream);
  }
}

function isHtmlErrorBody(body: string): boolean {
  const trimmed = body.trim().slice(0, 300).toLowerCase();
  return (
    trimmed.startsWith('<!doctype html') ||
    trimmed.startsWith('<html') ||
    trimmed.includes('<title>') ||
    trimmed.includes('<body')
  );
}

function statusFallbackMessage(status: number): string {
  if (status === 502 || status === 503 || status === 504) {
    return 'Grid2 is temporarily unavailable. Please retry in a moment.';
  }
  if (status >= 500) {
    return 'Grid2 could not complete the request. Please retry in a moment.';
  }
  return `Request failed with status ${status}`;
}

export function parseApiErrorBody(body: string, status: number): { message: string; code?: string } {
  const fallback = isHtmlErrorBody(body)
    ? statusFallbackMessage(status)
    : (body.trim() || statusFallbackMessage(status));

  try {
    const parsed = JSON.parse(body) as ErrorEnvelope;
    if (typeof parsed === "string") {
      return { message: parsed || fallback };
    }
    if (parsed && typeof parsed === "object") {
      const error = parsed.error;
      if (error && typeof error === "object") {
        return {
          message: error.message || fallback,
          ...(error.code ? { code: error.code } : {}),
        };
      }
      if (typeof error === "string" && error.trim()) {
        return {
          message: error,
          ...(typeof parsed.code === "string" ? { code: parsed.code } : {}),
        };
      }
      if (typeof parsed.message === "string" && parsed.message.trim()) {
        return {
          message: parsed.message,
          ...(typeof parsed.code === "string" ? { code: parsed.code } : {}),
        };
      }
    }
  } catch {
    // Non-JSON error body — use the sanitized text/status fallback below.
  }

  return { message: fallback };
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const normalized = normalizeApiPath(path);
  const res = await fetchWithTimeout(normalized.path, {
    ...options,
    credentials: "include",
    headers: mergeHeaders(
      {
        // Only declare a JSON content-type when a body is actually sent. A
        // bodyless request (e.g. DELETE) that still sets Content-Type:
        // application/json is rejected by the Fastify BFF as
        // FST_ERR_CTP_EMPTY_JSON_BODY (400) — which silently broke pipeline
        // deletion in the UI.
        ...(options.body !== undefined && options.body !== null
          ? { "Content-Type": "application/json" }
          : {}),
        ...(normalized.usesManagementProxy
          ? { [MANAGEMENT_PROXY_HEADER]: "1" }
          : {}),
      },
      options.headers,
    ),
  });

  if (res.status === 401) {
    window.location.href =
      path.startsWith("/mgmt/admin") || path.startsWith("/api/admin")
        ? "/admin/login"
        : "/login";
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    const body = await res.text();
    const { message, code } = parseApiErrorBody(body, res.status);
    if (isSmartsheetReconnectError({ code, message })) {
      redirectToSmartsheetConnect();
      throw new ApiError(res.status, "Redirecting to Smartsheet authorization", code);
    }
    throw new ApiError(res.status, message, code);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function adminHeaders(): HeadersInit {
  const token = localStorage.getItem("adminToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function adminFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  return apiFetch<T>(path, {
    ...options,
    headers: mergeHeaders(adminHeaders(), options.headers),
  });
}

/**
 * Fetch variant for public (unauthenticated) endpoints.
 * Does NOT redirect on 401 — public forms may legitimately receive 401
 * when OTP auth is required and the caller handles it explicitly.
 */
export async function publicFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetchWithTimeout(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.text();
    const { message, code } = parseApiErrorBody(body, res.status);
    throw new ApiError(res.status, message, code);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}
